import React, { useState, useEffect } from 'react';
import { Button, Card, Breadcrumb, Modal, Pagination, ListGroup, Form, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';
import { translations } from '../utils/translations';
import { RootState } from '../Redux/store';
import { useSelector, useDispatch } from 'react-redux';
import { FaWhatsapp, FaPhone, FaSms, FaTrash } from 'react-icons/fa'; // Icons for actions

interface Product {
    product_id: number;
    selling_price: number;
    quantity: number;
    product_name: string;
}

interface Sale {
    id: number;
    customer: string | null;
    walk_in_customer: string;
    products: Product[];
    total_amount: number;
    amount_paid: number;
    balance: number;
    status: string;
    customer_name: string;
    payment_type: string;
    reference_number: string;
    date_sold: string;
    phone_number: string | null; // Added phone_number
}

export default function DebtComponent() {
    const [sales, setSales] = useState<Sale[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [userRole, setUserRole] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const dispatch = useDispatch();
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];
    const [authenticated, setAuthenticated] = useState<boolean>(false);

    const getCookie = (name: string) => {
        const cookieName = name + '=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i].trim();
            if (cookie.startsWith(cookieName)) {
                return cookie.substring(cookieName.length);
            }
        }
        return '';
    };

    const fetchSales = async (page: number) => {
        setLoading(true);
        try {
            const token = getCookie('token');
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const response = await axios.get(
                `https://api.sominnovations.xyz/Branches/sales-with-balance/?page=${page}&customer_name=${searchTerm}`
            );
            setSales(response.data.results);
            setTotalPages(Math.ceil(response.data.count / 20));
        } catch (error) {
            console.error('Error fetching sales:', error);
        }
        setLoading(false);
    };

    const fetchUserRole = async () => {
        try {
            const token = getCookie('token');
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const { data } = await axios.get('https://api.sominnovations.xyz/users/user');
            setUserRole(data.user.role); // Set the user role
        } catch (error) {
            console.error('Failed to fetch user role:', error);
        }
    };

    useEffect(() => {
        fetchSales(currentPage);
        fetchUserRole(); // Fetch the user role when the component mounts
    }, [currentPage, searchTerm]); // Add searchTerm to dependencies

    const handleDeleteSale = async () => {
        if (selectedSale) {
            try {
                const token = getCookie('token');
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                await axios.delete(`https://api.sominnovations.xyz/Branches/sales-with-balance/${selectedSale.id}/`);
                setSales(sales.filter(sale => sale.id !== selectedSale.id));
                setSelectedSale(null);
                setShowCreateModal(false);
            } catch (error) {
                console.error('Error deleting sale:', error);
            }
        }
    };

    const handleSendReminder = (type: 'whatsapp' | 'sms' | 'call', phoneNumber: string | null) => {
        if (!phoneNumber) {
            alert('No phone number available for this customer.');
            return;
        }

        let message = `Dear customer, your outstanding balance is ${selectedSale?.balance}. Please make the payment at your earliest convenience.`;

        switch (type) {
            case 'whatsapp':
                window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
                break;
            case 'sms':
                window.open(`sms:${phoneNumber}?body=${encodeURIComponent(message)}`, '_blank');
                break;
            case 'call':
                window.open(`tel:${phoneNumber}`, '_blank');
                break;
            default:
                break;
        }
    };

    return (
        <div className="container mt-3 debts-container">
            <Card className="mb-4">
                <Card.Header style={{ backgroundColor: '#5271ff', color: '#fff' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4>{t.salesWithBalance}</h4>
                        <Breadcrumb>
                            <Breadcrumb.Item href="dashboard">
                                <p style={{ color: 'white' }}>{t.dashboard}</p>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>{t.sales}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                </Card.Header>
                <Card.Body>
                    {/* Search Bar */}
                    <Form.Control
                        type="text"
                        placeholder={t.searchCustomer}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="mb-3"
                    />
                </Card.Body>
            </Card>

            {loading ? (
                <div>Loading...</div>
            ) : (
                <>
                    <div className="table-responsive-sm">
                        <table className="table table-bordered">
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>{t.customers}</th>
                                    <th>{t.phoneNumber}</th>
                                    <th>{t.saleReference}</th>
                                    <th>{t.total}</th>
                                    <th>{t.amountPaid}</th>
                                    <th>{t.balance}</th>
                                    <th>{t.status}</th>
                                    <th>{t.datePaid}</th>
                                    <th>{t.actions}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sales.map((sale, index) => (
                                    <tr key={sale.id}>
                                        <td>{index + 1 + (currentPage - 1) * 20}</td>
                                        <td>{sale.customer_name || sale.walk_in_customer}</td>
                                        <td>{sale.phone_number || 'N/A'}</td>
                                        <td>{sale.reference_number}</td>
                                        <td>{sale.total_amount}</td>
                                        <td>{sale.amount_paid}</td>
                                        <td>{sale.balance}</td>
                                        <td>
                                            {sale.status === "unpaid" ? (
                                                <Badge bg="danger">{sale.status}</Badge>
                                            ) : sale.status === "partially paid" ? (
                                                <Badge bg="warning" text="dark">{sale.status}</Badge>
                                            ) : (
                                                <Badge bg="success">{sale.status}</Badge>
                                            )}
                                        </td>
                                        <td>{new Date(sale.date_sold).toLocaleString()}</td>
                                        <td>
                                            <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-whatsapp-${sale.id}`}>WhatsApp</Tooltip>}>
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleSendReminder('whatsapp', sale.phone_number)}
                                                >
                                                    <FaWhatsapp />
                                                </Button>
                                            </OverlayTrigger>
                                            <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-sms-${sale.id}`}>SMS</Tooltip>}>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleSendReminder('sms', sale.phone_number)}
                                                >
                                                    <FaSms />
                                                </Button>
                                            </OverlayTrigger>
                                            <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-call-${sale.id}`}>Call</Tooltip>}>
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleSendReminder('call', sale.phone_number)}
                                                >
                                                    <FaPhone />
                                                </Button>
                                            </OverlayTrigger>
                                            {userRole !== 'accountant' && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedSale(sale);
                                                        setShowCreateModal(true);
                                                    }}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination>
                        <Pagination.Prev
                            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        />
                        <Pagination.Item>{currentPage}</Pagination.Item>
                        <Pagination.Next
                            onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </>
            )}

            {/* Modal for Sale Details */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t.saleDetails}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ListGroup variant="flush">
                        {selectedSale?.products.map((product, index) => (
                            <ListGroup.Item key={index}>
                                <strong>{product.product_name}</strong> - ${product.selling_price} x {product.quantity}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                    <div className="mb-3">
                        <label>Customer</label>
                        <input
                            type="text"
                            className="form-control"
                            value={selectedSale?.customer_name || selectedSale?.walk_in_customer}
                            disabled
                        />
                    </div>
                    <div className="mb-3">
                        <label>Status</label>
                        <input
                            type="text"
                            className="form-control"
                            value={selectedSale?.status}
                            disabled
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        {t.close}
                    </Button>
                    {userRole !== 'accountant' && (
                        <Button variant="danger" onClick={handleDeleteSale}>
                            Delete Sale
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
}